import * as anodum from 'anodum';
import bowser from 'yola-bowser';
import throttle from 'lodash.throttle';
import core from '../core';
import text from '../core/hdrm/injections/bindings/text';
import mutation from '../core/hdrm/injections/bindings/mutation';
import preserveTextElement from '../core/hdrm/utils/helpers/preserve-text-element';
import updateChildrenPresence from '../core/hdrm/utils/helpers/update-children-presence';
import generateIdSuffix from '../core/hdrm/utils/helpers/generate-id-suffix';
import areChildrenEnabled from '../core/hdrm/verifiers/are-children-enabled';
import WSInternalEvent from '../core/hdrm/utils/helpers/ws-internal-event';
import areAllChildrenHidden from '../core/hdrm/verifiers/are-all-children-hidden';
import noop from '../utils/noop';
import validation from '../utils/validation';
import getURLProtocolScheme, { PROTOCOL_SCHEMES } from '../utils/get-url-protocol-scheme';
import forceHTTPS from '../utils/force-https';
import preventBodyScroll from '../utils/ios-workarounds/prevent-body-scroll';
import handleAsyncError from '../utils/handle-async-error';
import subscribeToExtensionsHooks from '../core/hooks/operations/subscribe-to-extensions-hooks';

export const hooks = {
  operations: {
    subscribe: subscribeToExtensionsHooks,
  },
};

export const extensions = {
  accessors: {
    getExtension: core.extensions.accessors.getExtension,
    getExtensionSettings: core.extensions.accessors.getExtensionSettings,
  },
  constants: {
    controlTypes: core.extensions.controlTypes,
  },
  operations: {
    registerExtension: core.extensions.operations.registerExtension,
  },
};

const { IMAGE_ACCEPT_TYPE, ALLOWED_IMAGE_FORMATS, DEFAULT_THUMBNAIL_SIZE } = core.assets.constants;

export const assets = {
  operations: {
    createMediaGalleryAssets: core.assets.operations.createMediaGalleryAssets,
    clientUploadAsset: core.assets.operations.clientUploadAsset,
    clientUploadAssets: core.assets.operations.clientUploadAssets,
  },
  helpers: {
    getImageOrientation: core.assets.helpers.getImageOrientation,
    getImageSize: core.assets.helpers.getImageSize,
    getNewAssetSrc: core.assets.helpers.getNewAssetSrc,
    addBaseHref: core.assets.helpers.addBaseHref,
    resetImageExifOrientation: core.assets.helpers.resetImageExifOrientation,
  },
  verifiers: {
    isExifRotationSupported: core.assets.verifiers.isExifRotationSupported,
    hasExifOrientation: core.assets.verifiers.hasExifOrientation,
  },
  constants: {
    IMAGE_ACCEPT_TYPE,
    ALLOWED_IMAGE_FORMATS,
    DEFAULT_THUMBNAIL_SIZE,
  },
};

export const blocks = {
  verifiers: {
    isElementBlock: core.blocks.verifiers.isElementBlock,
  },
  constants: core.blocks.constants,
};

export const dialogs = {
  operations: {
    register: core.dialogs.operations.register,
    show: core.dialogs.operations.show,
    hide: core.dialogs.operations.hide,
  },
  helpers: {
    getPositionByElement: core.dialogs.helpers.getPositionByElement,
  },
  verifiers: {
    isVisible: core.dialogs.verifiers.isVisible,
  },
};

export const site = {
  accessors: {
    getPages: core.site.accessors.getPages,
    getSiteId: core.site.accessors.getSiteId,
    getSiteName: core.site.accessors.getSiteName,
    getActivePageId: core.site.accessors.getActivePageId,
    getUserEmails: core.site.accessors.getUserEmails,
    getAssetsUrl: core.site.accessors.getAssetsUrl,
  },
  helpers: {
    composeAssetUrl: core.site.helpers.composeAssetUrl,
    fetchAsset: core.site.helpers.fetchAsset,
  },
  operations: {
    setPagesInNavigation: core.site.operations.setPagesInNavigation,
  },
};

export const customization = {
  accessors: {
    getCustomTextColors: core.customization.accessors.getCustomTextColors,
    getCurrentColorPalette: core.customization.accessors.getCurrentColorPalette,
    getCurrentFontPair: core.customization.accessors.getCurrentFontPair,
  },
  operations: {
    addCustomTextColor: core.customization.operations.addCustomTextColor,
  },
};

export const config = {
  accessors: {
    getAPIUrl: core.config.accessors.getAPIUrl,
  },
};

export const view = {
  accessors: {
    getElementAttribute: core.view.accessors.getElementAttribute,
    getElementBoundsInIframe: core.view.accessors.getElementBoundsInIframe,
    getLiveElement: core.view.accessors.getLiveElement,
    getStaticElement: core.view.accessors.getStaticElement,
    getLiveDocument: core.view.accessors.getLiveDocument,
    getLiveElementId: core.view.accessors.getLiveElementId,
    getSelection: core.view.accessors.getSelection,
    getScrollPosition: core.view.accessors.getScrollPosition,
    getElementOffset: core.view.accessors.getElementOffset,
  },
  operations: {
    bulkViewOperations: core.view.operations.bulkViewOperations,
    setElementAttribute: core.view.operations.setElementAttribute,
    setInlineElementStyle: core.view.operations.setInlineElementStyle,
    synchronizeElement: core.view.operations.synchronizeElement,
    replaceElement: core.view.operations.replaceElement,
    removeElementAttribute: core.view.operations.removeElementAttribute,
    setHoveredElement: core.view.operations.setHoveredElement,
    triggerSelectionUpdate: core.view.operations.triggerSelectionUpdate,
    insertBlock: core.view.operations.insertBlock,
    deleteElement: core.view.operations.deleteElement,
  },
  verifiers: {
    hasElementAttribute: core.view.verifiers.hasElementAttribute,
  },
  helpers: {
    smoothScroll: core.view.helpers.smoothScroll,
    getScrollableElement: core.view.helpers.getScrollableElement,
  },
};

const {
  renderStatuses,
  TEMPLATE_TAG,
  CUSTOM_HTML_TAG,
  RUNTIME_CONTAINER_CLASS,
  RUNTIME_RENDERING_ATTRIBUTE,
} = core.customHtml.constants;

export const customHtml = {
  operations: {
    setRenderStatus: core.customHtml.operations.setRenderStatus,
  },
  accessors: {
    getRenderStatus: core.customHtml.accessors.getRenderStatus,
  },
  constants: {
    renderStatuses,
    TEMPLATE_TAG,
    CUSTOM_HTML_TAG,
    RUNTIME_RENDERING_ATTRIBUTE,
    RUNTIME_CONTAINER_CLASS,
  },
  verifiers: {
    isRuntimeRender: core.customHtml.verifiers.isRuntimeRender,
  },
};

export const i18n = {
  accessors: {
    getLocale: core.i18n.accessors.getLocale,
  },
};

export const hdrm = {
  injections: {
    bindings: {
      text,
      mutation,
    },
  },
  utils: {
    helpers: {
      generateIdSuffix,
      preserveTextElement,
      updateChildrenPresence,
      WSInternalEvent,
      handleAsyncError,
    },
  },
  verifiers: {
    areChildrenEnabled,
    areAllChildrenHidden,
  },
  constants: {
    ...core.hdrm.constants.attributes,
    ...core.hdrm.constants.updateStrategies,
  },
};

export const textFormatting = {
  accessors: {
    getAdvancedSelection: core.textFormatting.accessors.getAdvancedSelection,
  },
  operations: {
    ...core.textFormatting.operations,
    registerWrapper: core.textFormatting.helpers.wrapperMap.registerWrapper,
    removeWrapper: core.textFormatting.helpers.wrapperMap.removeWrapper,
  },
  verifiers: {
    isWrappedBy: core.textFormatting.verifiers.isWrappedBy,
    isWrapperRegistered: core.textFormatting.verifiers.isWrapperRegistered,
  },
  helpers: {
    getCssVariablesMap: core.textFormatting.helpers.getCssVariablesMap,
    createCustomTextColorStylesheet: core.textFormatting.helpers.createCustomTextColorStylesheet,
    getCustomTextColorsStylesheet: core.textFormatting.helpers.getCustomTextColorsStylesheet,
    getCssTextFromCssVariablesMap: core.textFormatting.helpers.getCssTextFromCssVariablesMap,
    getCssVariableNameByColor: core.textFormatting.helpers.getCssVariableNameByColor,
    getCssRootVariablesMap: core.textFormatting.helpers.getCssRootVariablesMap,
    updateCustomColorStylesheetInGlobal:
      core.textFormatting.helpers.updateCustomColorStylesheetInGlobal,
    getCustomColorFromTextColor: core.textFormatting.helpers.getCustomColorFromTextColor,
    setRuleForSelector: core.textFormatting.helpers.setRuleForSelector,
    addRelatedClassesToElement: core.textFormatting.helpers.addRelatedClassesToElement,
    removeRelatedClassesFromElement: core.textFormatting.helpers.removeRelatedClassesFromElement,
    createRootVariableName: core.textFormatting.helpers.createRootVariableName,
    createCssTextRule: core.textFormatting.helpers.createCssTextRule,
    getTextVariableName: core.textFormatting.helpers.getTextVariableName,
  },
  constants: {
    wrappersTypes: core.textFormatting.constants.wrappersTypes,
  },
};

export const { serviceClient } = core;

export const utils = {
  throttle,
  noop,
  getURLProtocolScheme,
  forceHTTPS,
  PROTOCOL_SCHEMES,
  validation,
  preventBodyScroll,
  handleAsyncError,
};

export const contenteditableHistory = {
  operations: core.contentEditable.operations,
  helpers: {
    getFocusedElementInfo: core.contentEditable.helpers.getFocusedElementInfo,
    setSelectionByNodePath: core.contentEditable.helpers.setSelectionByNodePath,
  },
};

export const siteLocales = {
  accessors: core.siteLocales.accessors,
};

export const logger = {
  getLogger: core.logger.accessors.getLogger,
};

export { default as urlJoin } from 'url-join';

export { anodum, bowser };
